import { Button, TextInput } from 'flowbite-react';
import { debounce, get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { TbSearch } from 'react-icons/tb';
import { useCSVDownloader } from 'react-papaparse';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AMLeadGenPocContainer from 'src/components/LeadsGen/AMLeadGenPocContainer';
import CreateLeadGenModal from 'src/components/LeadsGen/CreateLeadGenModal';
import Container from 'src/components/shared/Container';
import UploadCsv from 'src/components/UploadCsv';
import { useToast } from 'src/context/ToastProvider';
import { useGetAllLeadGenSource, useGetAllLeadGenUsers, useGetLeadGenData, useUploadLeadGen } from 'src/hooks/leadGen.hooks';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { LeadData, Source, FilterLeadGenInput, TypeOfUsers, UserRole } from 'types';
import FilterDrawer from 'src/components/recruiterPositions/filterDrawer/FilterDrawer';
import LeadGenFilterForm from 'src/components/LeadsGen/LeadGenFilterForm';
import UpdatedUserModal from 'src/components/UpdatedUserModel';
import { useGetMyBDPocsQuery } from 'src/hooks/user.hooks';
import { useUserContext } from 'src/context/UserProvider';
import { Loader } from 'ui';

import { downloadCSV } from 'src/utils/downloadCsv';
import { useMutation } from 'react-query';
import { downloadLeadGenUsers } from 'src/apis/leadGen';
import BulkSelectLeadGenAction from 'src/components/LeadsGen/BulkSelectLeadGenAction';


const LeadsGenPOCsPage: React.FC = () => {
	const navigate = useNavigate();
	const { leadGenFilters, user } = useUserContext();

	const toast = useToast();
	const { userId } = useParams();
	const { CSVDownloader } = useCSVDownloader();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [leadGenData, setLeadGenData] = useState<LeadData[]>([]);
	const [searchParams, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(() => {
		const savedRowsPerPage = localStorage.getItem('leadGenRowsPerPage');
		return savedRowsPerPage ? parseInt(savedRowsPerPage, 10) : 5;
	});
	const [bulkSelectedIds, setBulkSelectedIds] = useState<string[]>([]);
	const bulkSelect = searchParams.get('bulkSelect') === 'true';

	useEffect(() => {
		localStorage.setItem('leadGenRowsPerPage', rowsPerPage.toString());
	}, [rowsPerPage]);

	const [filterApplied, setFilterApplied] = useState(false)

	const { data: myBDPOCsData } = useGetMyBDPocsQuery();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState<Record<string, string>[]>([]);
	const search = searchParams.get('search') || '';
	const [isReassignLeadGenPOCModal, setIsReassignLeadGenPOCModal] = useState(false);
	const [openAddLogModal, setOpenAddLogModal] = useState(false);

	const { data: allLeadGenData, isLoading: isAllLeadGenLoading, refetch } = useGetLeadGenData({
		...leadGenFilters as FilterLeadGenInput,
		search: searchParams.get('search') || '',
		page,
		filterLimit: rowsPerPage,
	});

	const { data: allLeadGensSource } = useGetAllLeadGenSource();


	const {
		mutate: uploadLeadGen,
		isLoading,
		isError,
		isSuccess,
	} = useUploadLeadGen(
		data => {
			setModalData(data?.UpdatedData || []);
			setLeadGenData(prevData => [...prevData, ...(Array.isArray(data) ? data : [])]);
			refetch();
			setIsModalOpen(true);
			toast({ title: 'Lead Gen data uploaded successfully', type: 'success' });
			// setIsModalOpen(false);

		},
		() => {
			refetch();
			toast({ title: 'Error uploading CSV data', type: 'error' });
		},
	);

	const { data: allLeadGensUser } = useGetAllLeadGenUsers();

	const downloadLeadGenUsersFn = useMutation(['downloadLeadGenUsers'], downloadLeadGenUsers, {
		onSuccess(data) {
			const csvData = data?.csvData.map((item: any) => ({
				contactname: String(item.contactname),
				contactemail: String(item.contactemail),
				contactphone: String(item.contactphone),
				company: String(item.company),
				alternativeName1: String(item.alternativeName1),
				alternativeEmail1: String(item.alternativeEmail1),
				alternativePhone1: String(item.alternativePhone1),
				alternativeName2: String(item.alternativeName2),
				alternativeEmail2: String(item.alternativeEmail2),
				alternativePhone2: String(item.alternativePhone2),
				companyurl: String(item.companyurl),
				source: String(item.source),
				callLogStatus: String(item.callLogStatus),
				jobpostinglink: String(item.jobpostinglink),
				leadgenpoc: String(item.leadgenpoc),
				status: String(item.status),
				assignedtoleadgen: String(item.assignedtoleadgen),
				bdPOC: String(item.bdPOC),
				assignedtoBD: String(item.assignedtoBD),
				lastremark: String(item.lastremark),
			}));
			downloadCSV({
				data: csvData,
				columns: [
					'contactname',
					'contactemail',
					'contactphone',
					'alternativeName1',
					'alternativeEmail1',
					'alternativePhone1',
					'alternativeName2',
					'alternativeEmail2',
					'alternativePhone2',
					'company',
					'companyurl',
					'source',
					'callLogStatus',
					'jobpostinglink',
					'leadgenpoc',
					'status',
					'assignedtoleadgen',
					'bdPOC',
					'assignedtoBD',
					'lastremark',
				],
			});
			toast({
				title: 'CSV Downloaded Successfully!',
				type: 'success',
			});
		},
		onError(error: any) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured while downloading CSV ${errorMessage}`,
				type: 'error',
			});
		},
	});

	useEffect(() => {
		if (allLeadGenData?.leadGens) {
			setLeadGenData(allLeadGenData?.leadGens);
		}
		refetch();
	}, [allLeadGenData, searchParams]);

	const handleCSVUpload = (data: Record<string, string>[]) => {
		const leadData = data.map(item => {
			return {
				name: item.name,
				sourceCompanyName: item.sourceCompanyName,
				email: item.email,
				phone: item.phone || Number(item.phone),
				alternativeName1: item.alternativeName1,
				alternativeEmail1: item.alternativeEmail1,
				alternativePhone1: item.alternativePhone1 || Number(item.alternativePhone1),
				alternativeName2: item.alternativeName2,
				alternativeEmail2: item.alternativeEmail2,
				alternativePhone2: item.alternativePhone2 || Number(item.alternativePhone2),
				jobPostingLink: item.jobPostingLink,
				leadGenPOC: item.leadGenPOC,
				source: item.source as Source,
				companyUrl: item.companyUrl,
			};
		});
		uploadLeadGen({ data: leadData as LeadData[] });
	};

	const sampleData: LeadData[] = [
		{
			name: 'John Doe',
			sourceCompanyName: 'Company A',
			email: 'test1@gmail.com',
			phone: 987654321,
			alternativeName1: 'John Doe',
			alternativeEmail1: 'test2@gmail.com',
			alternativePhone1: 987654321,
			alternativeName2: 'John Doe',
			alternativeEmail2: 'test3@gmail.com',
			alternativePhone2: 987654321,
			jobPostingLink: 'http://example.com/job1',
			leadGenPOC: 'masaitest@gmail.com',
			source: Source.LinkedInAds,
			companyUrl: 'http://google.com',
		},
	];
	const convertToCSVFormat = (data: LeadData[]) => {
		return data.map(item => ({
			name: item.name,
			sourceCompanyName: item.sourceCompanyName,
			email: item.email,
			phone: item.phone,
			alternativeName1: item.alternativeName1,
			alternativeEmail1: item.alternativeEmail1,
			alternativePhone1: item.alternativePhone1,
			alternativeName2: item.alternativeName2,
			alternativeEmail2: item.alternativeEmail2,
			alternativePhone2: item.alternativePhone2,
			jobPostingLink: item.jobPostingLink,
			leadGenPOC: item.leadGenPOC,
			source: item.source,
			companyUrl: item.companyUrl,
		}));
	};

	const leadDataSample: LeadData[] = sampleData.map(item => ({
		name: item.name,
		sourceCompanyName: item.sourceCompanyName,
		email: item.email,
		phone: item.phone,
		alternativeName1: item.alternativeName1,
		alternativeEmail1: item.alternativeEmail1,
		alternativePhone1: item.alternativePhone1,
		alternativeName2: item.alternativeName2,
		alternativeEmail2: item.alternativeEmail2,
		alternativePhone2: item.alternativePhone2,
		source: item.source,
		jobPostingLink: item.jobPostingLink,
		leadGenPOC: item.leadGenPOC,
		companyUrl: item.companyUrl,
	}));

	const csvData = convertToCSVFormat(leadDataSample);

	const searchChangeHandler = (search: string) => {
		searchParams.set('search', search);
		setSearchParams(searchParams);
		setPage(0);
		setRowsPerPage(5);
		refetch();
	};
	const debouncedChangeHandler = useCallback(debounce(searchChangeHandler, 300), [userId, searchParams]);


	const handlePageChange = () => {
		// setPage(page);
	}

	return (
		<>
			<Container>
				<div className="flex flex-col md:flex-row justify-start md:justify-between md:items-center py-2 pt-6 sm:pt-[10px] border-b border-[#E4E4EB] dark:border-[#555770] border-solid">
					<h4 className="text-xl font-semibold dark:text-white text-[#1C1C28] mb-2 md:mb-0">Leads</h4>
					<div className="flex gap-2 flex-wrap">
						<CSVDownloader filename="sample-leads-gen-pocs.csv" data={csvData}>
							<Button color="light">Download Sample CSV</Button>
						</CSVDownloader>
						<UploadCsv title="Bulk Upload Leads CSV" color="dark" handleSubmit={handleCSVUpload} header="Add Lead Gen" />
						<Button
							color="purplePlaced"
							onClick={onOpen}
							className="focus-visible:after:top-0 focus-visible:after:left-0 focus-visible:ring-purple-300 focus-visible:ring-2 after:content-[''] after:absolute after:left-[5%] after:w-full after:h-full after:top-[14%] active:shadow-[inset_0_0_8px_black] transition-shadow duration-200 focus:ring-0 after:z-[-3] after:rounded-[inherit] after:transition-all after:duration-200 hover:after:top-[0%] hover:after:left-[0%] active:after:top-[0%] active:after:left-[0%]"
						>
							Create Lead
						</Button>
					</div>
				</div>
				<div className="flex items-center gap-2 my-4">
					<TextInput
						id="search-candidate"
						placeholder="Search by email, phone, company"
						className="w-full max-w-[531px] [&_input]:bg-white flex-1 md:flex-none"
						defaultValue={search}
						icon={() => <TbSearch color="#C7C9D9" />}
						onChange={e => debouncedChangeHandler(e.target.value)}
					/>
					<FilterDrawer
						filterDrawerHeader={
							<h1 className="text-[#1C1C28] dark:text-inherit text-lg font-inter font-[500] px-5 pt-4 md:border-b md:pb-4">
								Filter by
							</h1>
						}
						filterDrawerBody={(filter: any) => {
							return (
								<div className="px-3 py-4">
									<LeadGenFilterForm
										onApply={() => {
											setFilterApplied(true);
											filter.setFilterApplied(true);
										}}
										onClear={() => {
											setFilterApplied(false);
											filter.setFilterApplied(false);
										}}
										onClose={() => {
											filter.setShowFilterDrawer(false);
										}}
										allLeadGensUser={allLeadGensUser?.data || []}
										myBDPOCsData={{ bds: myBDPOCsData?.bds || [] }}
										allLeadGensSource={Array.isArray(allLeadGensSource?.data) ? allLeadGensSource?.data : []}
									/>
								</div>
							);
						}}
					/>
					<Button
						color="light"
						disabled={downloadLeadGenUsersFn.isLoading}
						isProcessing={downloadLeadGenUsersFn.isLoading}
						onClick={() => {
							downloadLeadGenUsersFn.mutate({
								...leadGenFilters as FilterLeadGenInput,
								search: searchParams.get('search') || '',
							});
						}}
					>
						Download CSV
					</Button>
					<div>
						<BulkSelectLeadGenAction
							typeOfUsers={UserRole.SUPER_LEAD_GEN}
							bulkSelectedIds={bulkSelectedIds}
							setBulkSelectedIds={setBulkSelectedIds}
							setIsReassignLeadGenPOCModal={setIsReassignLeadGenPOCModal}
							setOpenAddLogModal={setOpenAddLogModal}
						/>
					</div>
				</div>
				{isAllLeadGenLoading ? (
					<div className="flex items-center justify-center relative top-[40vh]">
						<Loader />
					</div>
				) : (
					<AMLeadGenPocContainer
						refetch={refetch}
						data={leadGenData}
						myBDPOCsData={myBDPOCsData}
						onPageChange={handlePageChange}
						totalCount={allLeadGenData?.totalCount || 0}
						showBulkSelectAction={bulkSelect}
						page={page}
						setPage={setPage}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={setRowsPerPage}
						role={user?.role || ""}
						bulkSelectedIds={bulkSelectedIds}
						setBulkSelectedIds={setBulkSelectedIds}
						setIsReassignLeadGenPOCModal={setIsReassignLeadGenPOCModal}
						setOpenAddLogModal={setOpenAddLogModal}
						openAddLogModal={openAddLogModal}
						isReassignLeadGenPOCModal={isReassignLeadGenPOCModal}
					/>
				)}
			</Container>
			<CreateLeadGenModal
				isOpen={isOpen}
				onClose={onClose}
				refetch={refetch}
				allLeadGensUser={allLeadGensUser?.data || []}
				curr_user={{
					id: user?._id || "",
					name: user?.name || ""
				}}
			/>


			{isModalOpen && (
				<UpdatedUserModal
					isOpen={isModalOpen}
					data={modalData}
					header="Updated Lead Gen Data"
					onClose={() => setIsModalOpen(false)}
				/>
			)}
		</>
	);
};

export default LeadsGenPOCsPage;
